import type { FrequentStation } from '~/types/common/frequentStation';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';

/** よく使う駅を保存するlocalStorageキーのプレフィックス（実際のキーはユーザのPLAT IDを付与する） */
const STORAGE_KEY_PREFIX = 'frequentStations.';

/**
 * よく使う駅（作業駅の選択履歴）の保存・集計を行うcomposable
 *
 * localStorageへのアクセスはuseLocalStorageLogic経由で行い、
 * ログインユーザ（PLAT ID）ごとにキーを分けて保存する。
 */
export const useFrequentStationLogic = () => {
  const { getItem, setItem } = useLocalStorageLogic();
  const loginUserStore = useLoginUserStore();

  /**
   * ログインユーザのPLAT IDに対応するlocalStorageキーを生成する
   *
   * @param platId ログインユーザのPLAT ID
   * @returns localStorageキー
   */
  const _buildKey = (platId: string): string => `${STORAGE_KEY_PREFIX}${platId}`;

  /**
   * 保存済みの履歴レコードを読み込む
   *
   * @param platId ログインユーザのPLAT ID
   * @returns 履歴レコード配列（未保存・破損時は空配列）
   */
  const _load = (platId: string): FrequentStation[] => {
    const raw = getItem(_buildKey(platId));
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
    catch {
      // 破損データは空として扱う
      return [];
    }
  };

  /**
   * 履歴レコードを保存する
   *
   * @param platId ログインユーザのPLAT ID
   * @param records 保存する履歴レコード配列
   */
  const _save = (platId: string, records: FrequentStation[]): void => {
    setItem(_buildKey(platId), JSON.stringify(records));
  };

  /**
   * 駅の選択を履歴に記録する（選択回数を加算する）
   *
   * @param station 選択された駅情報
   */
  const recordSelection = (station: StationWithOrganization): void => {
    const platId = loginUserStore.loginUser?.platId;
    if (!platId) {
      return;
    }

    const records = _load(platId);
    const existing = records.find((record) =>
      record.stationCd === station.stationCd && record.stationSubCd === station.stationSubCd,
    );

    if (existing) {
      // マスタ更新（駅名・支社の変更）に追従しつつ選択回数を加算する
      Object.assign(existing, station);
      existing.count += 1;
      existing.lastSelectedAt = Date.now();
    }
    else {
      records.push({
        ...station,
        count: 1,
        lastSelectedAt: Date.now(),
      });
    }

    _save(platId, records);
  };

  /**
   * 選択回数の多い順に上位の駅を取得する（同数の場合は最終選択日時の新しい順）
   *
   * @param limit 取得する最大件数
   * @returns 選択回数降順・最終選択日時降順の履歴レコード配列
   */
  const getTopStations = (limit: number): FrequentStation[] => {
    const platId = loginUserStore.loginUser?.platId;
    if (!platId) {
      return [];
    }

    // 選択回数が同数の場合は、直近に選択した駅を先頭にする
    return [..._load(platId)]
      .sort((a, b) => b.count - a.count || b.lastSelectedAt - a.lastSelectedAt)
      .slice(0, limit);
  };

  return {
    recordSelection,
    getTopStations,
  };
};
